<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSensorStore } from '@/store/SensorStore';
import { formatTimestamp } from "@/plugins/utils";

// PrimeVue Components
import Tag from 'primevue/tag';
import Dropdown from 'primevue/dropdown';
import Skeleton from 'primevue/skeleton';

// Initialization
const router = useRouter();
const sensorStore = useSensorStore();

// State
const searchQuery = ref('');
const layout = ref('grid');
const sortKey = ref('name');
const sortOrder = ref(1);
const sortField = ref('name');
const filterStatus = ref('all');
const sortOptions = ref([
  { label: 'Name (A-Z)', value: 'name' },
  { label: 'Name (Z-A)', value: '!name' },
  { label: 'Status', value: 'status' },
  { label: 'Last Seen', value: '!last_seen' },
  { label: 'Location', value: 'location' },
]);
const statusOptions = ref([
  { label: 'All Status', value: 'all' },
  { label: 'Online', value: 'online' },
  { label: 'Offline', value: 'offline' },
  { label: 'Pending', value: 'pending' },
]);

// Fetch data on component mount
onMounted(() => {
  sensorStore.fetchSensors();
});

const onSortChange = (event) => {
  const value = event.value;
  if (value.indexOf('!') === 0) {
    sortOrder.value = -1;
    sortField.value = value.substring(1, value.length);
    sortKey.value = value;
  } else {
    sortOrder.value = 1;
    sortField.value = value;
    sortKey.value = value;
  }
};

// Computed property to filter and sort sensors
const processedSensors = computed(() => {
  if (sensorStore.isLoadingList) {
    return [];
  }
  const initialData = Array.isArray(sensorStore.sensors) ? sensorStore.sensors : [];
  let data = initialData.filter(sensor => sensor && sensor.unique_id);

  // Filter by status
  if (filterStatus.value && filterStatus.value !== 'all') {
    data = data.filter(sensor => sensor.status === filterStatus.value);
  }

  // Filter by search query
  if (searchQuery.value) {
    const lowerCaseQuery = searchQuery.value.toLowerCase();
    data = data.filter(sensor =>
      sensor.name?.toLowerCase().includes(lowerCaseQuery) ||
      (sensor.location || '').toLowerCase().includes(lowerCaseQuery) ||
      (sensor.tags && sensor.tags.some(tag => tag.toLowerCase().includes(lowerCaseQuery)))
    );
  }

  // Sort
  if (sortField.value) {
    data.sort((a, b) => {
      const valA = a[sortField.value] || '';
      const valB = b[sortField.value] || '';
      let comparison = 0;
      if (valA > valB) comparison = 1;
      else if (valA < valB) comparison = -1;
      return comparison * sortOrder.value;
    });
  }

  return data;
});

// Stats
const sensorStats = computed(() => {
  const all = Array.isArray(sensorStore.sensors) ? sensorStore.sensors : [];
  return {
    total: all.length,
    online: all.filter(s => s?.status === 'online').length,
    offline: all.filter(s => s?.status === 'offline').length,
    pending: all.filter(s => s?.status === 'pending').length,
  };
});

// Navigation methods
const goToAddSensor = () => {
  router.push({ name: 'Add new sensor' });
};

const goToSensorDetails = (uniqueId) => {
  router.push({ name: 'Edit Sensor', params: { unique_id: uniqueId } });
};

const getStatusIcon = (status) => {
  const icons = {
    online: 'fa-check-circle',
    offline: 'fa-times-circle',
    pending: 'fa-clock',
  };
  return icons[status] || 'fa-question-circle';
};

const getSensorTypeIcon = (type) => {
  const icons = {
    temperature: 'fa-thermometer-half',
    humidity: 'fa-tint',
    pressure: 'fa-compress-arrows-alt',
    motion: 'fa-running',
    light: 'fa-sun',
    sound: 'fa-volume-up',
    gas: 'fa-smog',
    default: 'fa-microchip',
  };
  return icons[type] || icons.default;
};
</script>

<template>
  <div class="py-4 container-fluid">
    <div class="card">

      <div class="card-header border-bottom">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <div class="mb-3 mb-md-0">
            <h6 class="mb-0">Sensors</h6>
            <p class="text-sm mb-0">Monitor and manage your IoT sensor network.</p>
          </div>
          <button class="btn mb-0 bg-gradient-primary btn-md" @click="goToAddSensor">
            <i class="fas fa-plus"></i> &nbsp;
            <span>Add Sensor</span>
          </button>

        </div>

        <!-- Stats Cards -->
        <div class="stats-grid mt-4">
          <div class="stat-card" @click="filterStatus = 'all'">
            <div class="stat-icon total">
              <i class="fas fa-microchip"></i>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ sensorStats.total }}</span>
              <span class="stat-label">Total Sensors</span>
            </div>
          </div>
          <div class="stat-card" @click="filterStatus = 'online'">
            <div class="stat-icon online">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ sensorStats.online }}</span>
              <span class="stat-label">Online</span>
            </div>
          </div>
          <div class="stat-card" @click="filterStatus = 'offline'">
            <div class="stat-icon offline">
              <i class="fas fa-times-circle"></i>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ sensorStats.offline }}</span>
              <span class="stat-label">Offline</span>
            </div>
          </div>
          <div class="stat-card" @click="filterStatus = 'pending'">
            <div class="stat-icon pending">
              <i class="fas fa-clock"></i>
            </div>
            <div class="stat-info">
              <span class="stat-value">{{ sensorStats.pending }}</span>
              <span class="stat-label">Pending</span>
            </div>
          </div>
        </div>

        <!-- Filters & Controls -->
        <div class="controls-bar">
          <div class="search-wrapper">
          <span class="search-icon">
            <i class="fas fa-search"></i>
          </span>
            <input
                v-model="searchQuery"
                type="text"
                placeholder="    Search sensors..."
                class="search-input"
            />
          </div>
          <div class="filter-controls">
            <Dropdown
                v-model="filterStatus"
                :options="statusOptions"
                optionLabel="label"
                optionValue="value"
                class="filter-dropdown"
            />
            <Dropdown
                v-model="sortKey"
                :options="sortOptions"
                optionLabel="label"
                optionValue="value"
                placeholder="Sort By"
                @change="onSortChange($event)"
                class="sort-dropdown"
            />
            <div class="view-toggle">
              <button
                  :class="['toggle-btn', { active: layout === 'grid' }]"
                  @click="layout = 'grid'"
                  title="Grid view"
              >
                <i class="fas fa-th-large"></i>
              </button>
              <button
                  :class="['toggle-btn', { active: layout === 'list' }]"
                  @click="layout = 'list'"
                  title="List view"
              >
                <i class="fas fa-list"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

        <!-- Loading State -->
        <div v-if="sensorStore.isLoadingList" class="sensors-grid">
          <div v-for="n in 6" :key="n" class="sensor-card skeleton-card">
            <div class="card-header">
              <Skeleton width="60%" height="1.5rem" />
              <Skeleton width="4rem" height="1.5rem" borderRadius="1rem" />
            </div>
            <div class="card-body">
              <Skeleton width="80%" height="1rem" class="mb-3" />
              <Skeleton width="100%" height="4rem" class="mb-3" />
              <Skeleton width="50%" height="0.875rem" />
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="processedSensors.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="fas fa-satellite-dish"></i>
          </div>
          <h3>No sensors found</h3>
          <p v-if="searchQuery || filterStatus !== 'all'">
            Try adjusting your search or filter criteria
          </p>
          <p v-else>Get started by adding your first sensor</p>
          <button v-if="!searchQuery && filterStatus === 'all'" class="btn-add-sensor" @click="goToAddSensor">
            <i class="fas fa-plus"></i>
            <span>Add Your First Sensor</span>
          </button>
          <button v-else class="btn-clear-filters" @click="searchQuery = ''; filterStatus = 'all'">
            <i class="fas fa-times"></i>
            <span>Clear Filters</span>
          </button>
        </div>


      <!-- Grid View -->
      <div v-if="layout === 'grid'" class="sensors-grid">
        <div
            v-for="sensor in processedSensors"
            :key="sensor.unique_id"
            class="sensor-card"
            :class="'status-' + sensor.status"
            @click="goToSensorDetails(sensor.unique_id)"
        >
          <div class="card-header">
            <div class="sensor-icon">
              <i :class="['fas', getSensorTypeIcon(sensor.type)]"></i>
            </div>
            <span class="status-badge" :class="sensor.status">
              <i :class="['fas', getStatusIcon(sensor.status)]"></i>
              {{ sensor.status }}
            </span>
          </div>
          <div class="card-body">
            <h3 class="sensor-name">{{ sensor.name }}</h3>
            <p class="sensor-location">
              <i class="fas fa-map-marker-alt"></i>
              {{ sensor.location || 'No location set' }}
            </p>
            <div class="sensor-tags" v-if="sensor.tags && sensor.tags.length">
              <Tag
                  v-for="tag in sensor.tags.slice(0, 3)"
                  :key="tag"
                  :value="tag.name"
                  class="sensor-tag"
              />
              <span v-if="sensor.tags.length > 3" class="more-tags">
                +{{ sensor.tags.length - 3 }}
              </span>
            </div>
            <div class="sensor-tags" v-else>
              <span class="no-tags">No tags</span>
            </div>
          </div>
          <div class="card-footer">
            <div class="last-seen">
              <i class="fas fa-clock"></i>
              <span v-if="sensor.last_seen && sensor.last_seen !== 'Not seen recently'">
                {{ formatTimestamp(sensor.last_seen) }}
              </span>
              <span v-else class="never-seen">Never seen</span>
            </div>
            <button class="btn-view" @click.stop="goToSensorDetails(sensor.unique_id)">
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="sensors-list">
        <div class="list-header">
          <span class="col-name">Sensor</span>
          <span class="col-location">Location</span>
          <span class="col-status">Status</span>
          <span class="col-tags">Tags</span>
          <span class="col-seen">Last Seen</span>
          <span class="col-actions">Actions</span>
        </div>
        <div
            v-for="sensor in processedSensors"
            :key="sensor.unique_id"
            class="list-row"
            :class="'status-' + sensor.status"
            @click="goToSensorDetails(sensor.unique_id)"
        >
          <div class="col-name">
            <div class="sensor-icon-sm">
              <i :class="['fas', getSensorTypeIcon(sensor.type)]"></i>
            </div>
            <span class="sensor-name">{{ sensor.name }}</span>
          </div>
          <div class="col-location">
            <i class="fas fa-map-marker-alt"></i>
            {{ sensor.location || 'N/A' }}
          </div>
          <div class="col-status">
            <span class="status-badge" :class="sensor.status">
              <i :class="['fas', getStatusIcon(sensor.status)]"></i>
              {{ sensor.status }}
            </span>
          </div>
          <div class="col-tags">
            <Tag
                v-for="tag in (sensor.tags || []).slice(0, 2)"
                :key="tag"
                :value="tag"
                class="sensor-tag"
            />
            <span v-if="sensor.tags && sensor.tags.length > 2" class="more-tags">
              +{{ sensor.tags.length - 2 }}
            </span>
            <span v-if="!sensor.tags || !sensor.tags.length" class="no-tags">—</span>
          </div>
          <div class="col-seen">
            <template v-if="sensor.last_seen && sensor.last_seen !== 'Not seen recently'">
              {{ formatTimestamp(sensor.last_seen) }}
            </template>
            <span v-else class="never-seen">Never</span>
          </div>
          <div class="col-actions">
            <button class="btn-action" @click.stop="goToSensorDetails(sensor.unique_id)" title="View details">
              <i class="fas fa-eye"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Results count -->
      <div v-if="!sensorStore.isLoadingList && processedSensors.length > 0" class="results-info">
        Showing {{ processedSensors.length }} of {{ sensorStats.total }} sensors
      </div>
    </div>
  </div>
</template>

<style scoped>
.sensors-page {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-text h1 {
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.25rem 0;
  color: var(--calid-text-primary, #344767);
}

.header-text p {
  margin: 0;
  color: var(--calid-text-secondary, #67748e);
  font-size: 0.95rem;
}

.btn-add-sensor {
  //display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #00d4ff;
  color: #000;
  border: none;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-add-sensor:hover {
  background: #00b8e6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 212, 255, 0.3);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }
}

.stat-card {
  background: var(--calid-card-bg, #fff);
  border-radius: 1rem;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--calid-border, #e9ecef);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.stat-icon.total {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.stat-icon.online {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.stat-icon.offline {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.stat-icon.pending {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--calid-text-primary);
  line-height: 1.2;
}

.stat-label {
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #67748e);
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

/* Controls Bar */
.controls-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  flex: 1;
  max-width: 400px;
  min-width: 200px;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #8898aa;
  font-size: 14px;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 44px;
  padding: 0 16px 0 44px;
  border: 1px solid var(--calid-border, #e9ecef);
  border-radius: 12px;
  font-size: 15px;
  background: var(--calid-card-bg, #fff);
  color: var(--calid-text-primary, #344767);
  outline: none;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.search-input::placeholder {
  color: #8898aa;
}

.search-input:focus {
  border-color: #00d4ff;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.15);
}

.filter-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-controls :deep(.p-dropdown) {
  min-width: 140px;
  border-radius: 0.75rem;
}

.view-toggle {
  display: flex;
  background: var(--calid-bg-tertiary, #f8f9fa);
  border-radius: 0.5rem;
  padding: 0.25rem;
}

.toggle-btn {
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--calid-text-tertiary, #8898aa);
  cursor: pointer;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background: var(--calid-card-bg, #fff);
  color: #00d4ff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.toggle-btn:hover:not(.active) {
  color: var(--calid-text-primary, #344767);
}

/* Sensors Grid */
.sensors-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.25rem;
}

.sensor-card {
  background: var(--calid-card-bg, #fff);
  border-radius: 1rem;
  border: 1px solid var(--calid-border, #e9ecef);
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding: 0.4em;
  margin: 1.5em;
}

.sensor-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--calid-text-tertiary, #8898aa);
  transition: background 0.2s ease;
}

.sensor-card.status-online::before {
  background: #10b981;
}

.sensor-card.status-offline::before {
  background: #ef4444;
}

.sensor-card.status-pending::before {
  background: #f59e0b;
}

.sensor-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.sensor-card .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.25rem 0.75rem;
}

.sensor-icon {
  width: 2.5rem;
  height: 2.5rem;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  font-size: 1.1rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 2rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.status-badge.online {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.status-badge.offline {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.status-badge.pending {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.sensor-card .card-body {
  padding: 0.75rem 1.25rem;
}

.sensor-name {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--calid-text-primary, #344767);
}

.sensor-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--calid-text-secondary, #67748e);
  margin: 0 0 1rem 0;
}

.sensor-location i {
  color: var(--calid-text-tertiary, #8898aa);
}

.sensor-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  min-height: 1.75rem;
  align-items: center;
}

.sensor-tag {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  background: var(--calid-bg-tertiary, #f1f5f9);
  color: var(--calid-text-secondary, #64748b);
  border-radius: 0.375rem !important;
  border: 1px solid var(--calid-cold-hover);
}

.more-tags {
  font-size: 0.75rem;
  color: var(--calid-text-tertiary, #8898aa);
  font-weight: 500;
}

.no-tags {
  font-size: 0.8rem;
  color: var(--calid-text-tertiary, #8898aa);
  font-style: italic;
}

.sensor-card .card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.25rem 1.25rem;
  border-top: 1px solid var(--calid-border, #e9ecef);
  margin-top: 0.75rem;
}

.last-seen {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--calid-text-tertiary, #8898aa);
}

.last-seen i {
  font-size: 0.75rem;
}

.never-seen {
  font-style: italic;
}

.btn-view {
  width: 2rem;
  height: 2rem;
  border: none;
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-view:hover {
  background: #00d4ff;
  color: #000;
}

/* List View */
.sensors-list {
  background: var(--calid-card-bg, #fff);
  border-radius: 1rem;
  border: 1px solid var(--calid-border, #e9ecef);
  overflow: hidden;
}

.list-header {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1.5fr 1fr 0.5fr;
  padding: 1rem 1.5rem;
  background: var(--calid-bg-tertiary, #f8f9fa);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--calid-text-tertiary, #8898aa);
  border-bottom: 1px solid var(--calid-border, #e9ecef);
}

.list-row {
  display: grid;
  grid-template-columns: 2fr 1.5fr 1fr 1.5fr 1fr 0.5fr;
  padding: 1rem 1.5rem;
  align-items: center;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--calid-border, #e9ecef);
  position: relative;
}

.list-row::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: transparent;
  transition: background 0.2s ease;
}

.list-row.status-online::before {
  background: #10b981;
}

.list-row.status-offline::before {
  background: #ef4444;
}

.list-row.status-pending::before {
  background: #f59e0b;
}

.list-row:last-child {
  border-bottom: none;
}

.list-row:hover {
  background: var(--calid-bg-tertiary, #f8f9fa);
}

.col-name {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.sensor-icon-sm {
  width: 2rem;
  height: 2rem;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.col-name .sensor-name {
  font-weight: 600;
  color: var(--calid-text-primary, #344767);
  font-size: 0.95rem;
  margin: 0;
}

.col-location {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--calid-text-secondary, #67748e);
}

.col-location i {
  color: var(--calid-text-tertiary, #8898aa);
  font-size: 0.8rem;
}

.col-status .status-badge {
  font-size: 0.7rem;
}

.col-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.col-seen {
  font-size: 0.8rem;
  color: var(--calid-text-secondary, #67748e);
}

.col-actions {
  display: flex;
  justify-content: flex-end;
}

.btn-action {
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  color: var(--calid-text-tertiary, #8898aa);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action:hover {
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--calid-card-bg, #fff);
  border-radius: 1rem;
  border: 1px solid var(--calid-border, #e9ecef);
}

.empty-icon {
  width: 5rem;
  height: 5rem;
  background: var(--calid-bg-tertiary, #f1f5f9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.empty-icon i {
  font-size: 2rem;
  color: var(--calid-text-tertiary, #8898aa);
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--calid-text-primary, #344767);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: var(--calid-text-secondary, #67748e);
  margin: 0 0 1.5rem 0;
}

.btn-clear-filters {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: var(--calid-bg-tertiary, #f1f5f9);
  color: var(--calid-text-primary, #344767);
  border: none;
  border-radius: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-clear-filters:hover {
  background: var(--calid-border, #e2e8f0);
}

/* Results Info */
.results-info {
  text-align: center;
  padding: 1rem;
  font-size: 0.875rem;
  color: var(--calid-text-tertiary, #8898aa);
}

/* Skeleton */
.skeleton-card .card-header,
.skeleton-card .card-body {
  padding: 1.25rem;
}

/* Dark mode */
:root.dark-version .sensors-page,
.dark-version .sensors-page {
  --calid-card-bg: #1a1f37;
  --calid-bg-tertiary: #111827;
  --calid-border: #2d3748;
  --calid-text-primary: #f1f5f9;
  --calid-text-secondary: #94a3b8;
  --calid-text-tertiary: #64748b;
}

:root.dark-version .stat-card,
.dark-version .stat-card {
  background: #1a1f37;
}

:root.dark-version .sensor-card,
.dark-version .sensor-card {
  background: #1a1f37;
}

:root.dark-version .sensors-list,
.dark-version .sensors-list {
  background: #1a1f37;
}

:root.dark-version .list-header,
.dark-version .list-header {
  background: #111827;
}

:root.dark-version .list-row:hover,
.dark-version .list-row:hover {
  background: #111827;
}

:root.dark-version .empty-state,
.dark-version .empty-state {
  background: #1a1f37;
}

:root.dark-version .view-toggle,
.dark-version .view-toggle {
  background: #111827;
}

:root.dark-version .toggle-btn.active,
.dark-version .toggle-btn.active {
  background: #1a1f37;
}

/* Responsive */
@media (max-width: 1200px) {
  .list-header,
  .list-row {
    grid-template-columns: 2fr 1.5fr 1fr 1fr 0.5fr;
  }
  .col-tags {
    display: none;
  }
  .list-header .col-tags {
    display: none;
  }
}

@media (max-width: 768px) {
  .sensors-page {
    padding: 1rem;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
  }

  .btn-add-sensor {
    justify-content: center;
  }

  .controls-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: none;
  }

  .filter-controls {
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .sensors-grid {
    grid-template-columns: 1fr;
  }

  .list-header,
  .list-row {
    grid-template-columns: 1fr auto auto;
  }

  .col-location,
  .col-seen {
    display: none;
  }

  .list-header .col-location,
  .list-header .col-seen {
    display: none;
  }
}
</style>
